/**
 * supabase-js RESOLVES with `{ error }` on a failed request rather than throwing — a dropped
 * connection, a gateway 5xx, an RLS refusal all come back as a value. Destructuring only `data`
 * therefore turns a failed write into a silent no-op, and the caller happily reports success.
 * That is the bug class behind the coach-editor cascade failures and the ProgramBuilder save.
 *
 * Use this to make a write loud:
 *
 *   assertWriteOk('Saving week 2', await supabase.from('program_weeks').update({...}).eq(...));
 *
 * For an insert it also returns `data`, so the row is not read off a null:
 *
 *   const week = assertWriteOk('Creating week 2', await supabase...insert({...}).select('id').single());
 */

/**
 * Throws if a Supabase result carries an error; otherwise returns its data.
 *
 * The thrown Error is tagged `partialSave = true`. Callers running a sequence of writes with no
 * transaction use that to tell the user the work is half-applied, rather than implying nothing
 * changed — the difference between re-opening the editor to check and walking away.
 *
 * @param {string} label - What was being attempted, e.g. 'Saving week 2'. Prefixed to the message.
 * @param {{ data?: any, error?: { message?: string } | null }} result - An awaited supabase call.
 * @returns {any} result.data
 * @throws {Error & { partialSave: true, cause?: any }}
 */
export const assertWriteOk = (label, result) => {
  const { data, error } = result || {};
  if (error) {
    const err = new Error(`${label}: ${error.message || 'write failed'}`);
    err.partialSave = true;
    err.cause = error;
    throw err;
  }
  return data;
};
